import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CATEGORY_OPTIONS = [
  { value: 'networking', label: 'Networking' },
  { value: 'security', label: 'Security' },
  { value: 'cloud', label: 'Cloud' },
  { value: 'software', label: 'Software' },
  { value: 'telecom', label: 'Telecom' },
  { value: 'iot', label: 'IoT' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'other', label: 'Other' },
];

const LICENSING_OPTIONS = [
  { value: 'capex', label: 'CAPEX' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'saas', label: 'SaaS' },
  { value: 'perpetual', label: 'Perpetual' },
];

const MARGIN_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'sunset', label: 'Sunset' },
  { value: 'draft', label: 'Draft' },
];

const getCategoryColor = (cat) => {
  const map = {
    networking: 'bg-blue-100 text-blue-700',
    security: 'bg-red-100 text-red-700',
    cloud: 'bg-purple-100 text-purple-700',
    software: 'bg-green-100 text-green-700',
    telecom: 'bg-orange-100 text-orange-700',
    iot: 'bg-cyan-100 text-cyan-700',
    hardware: 'bg-slate-100 text-slate-700',
    other: 'bg-gray-100 text-gray-700',
  };
  return map?.[cat] || 'bg-muted text-muted-foreground';
};

const getStatusColor = (status) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    sunset: 'bg-amber-100 text-amber-700 border-amber-200',
    draft: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return map?.[status] || 'bg-muted text-muted-foreground border-transparent';
};

const getMarginColor = (margin) => {
  const map = {
    high: 'text-emerald-600',
    medium: 'text-amber-600',
    low: 'text-red-500',
  };
  return map?.[margin] || 'text-slate-500';
};

export default function ProductDetailModal({ product, onClose, onUpdate, onDelete, onArchive }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [confirmAction, setConfirmAction] = useState(null); // 'delete' | 'archive'
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const overlayRef = useRef(null);
  const statusRef = useRef(null);

  useEffect(() => {
    if (product) {
      setFormData({
        productName: product?.productName || '',
        category: product?.category || '',
        oemBrand: product?.oemBrand || '',
        modelSku: product?.modelSku || '',
        licensingModel: product?.licensingModel || '',
        description: product?.description || '',
        targetIndustry: product?.targetIndustry || '',
        priceRange: product?.priceRange || '',
        marginCategory: product?.marginCategory || 'medium',
        catalogStatus: product?.catalogStatus || 'active',
      });
    }
    setIsEditMode(false);
    setConfirmAction(null);
  }, [product]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e?.key === 'Escape') {
        if (confirmAction) setConfirmAction(null);
        else if (isEditMode) setIsEditMode(false);
        else onClose?.();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [confirmAction, isEditMode, onClose]);

  // Close status dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (statusRef?.current && !statusRef?.current?.contains(e?.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (!product) return null;

  const handleField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!formData?.productName?.trim()) return;
    setSaving(true);
    try {
      await onUpdate?.(product?.id, formData);
      setIsEditMode(false);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusDropdownOpen(false);
    await onUpdate?.(product?.id, { ...formData, catalogStatus: newStatus });
    setFormData(prev => ({ ...prev, catalogStatus: newStatus }));
  };

  const handleArchiveConfirm = async () => {
    await onArchive?.(product?.id);
    setConfirmAction(null);
    onClose?.();
  };

  const handleDeleteConfirm = async () => {
    await onDelete?.(product?.id);
    setConfirmAction(null);
    onClose?.();
  };

  const displayData = isEditMode ? formData : product;
  const categoryLabel = CATEGORY_OPTIONS?.find(c => c?.value === displayData?.category)?.label || displayData?.category;
  const licensingLabel = LICENSING_OPTIONS?.find(l => l?.value === displayData?.licensingModel)?.label || displayData?.licensingModel;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[200] flex items-center justify-center p-16"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e?.target === overlayRef?.current && !confirmAction) onClose?.(); }}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{ animation: 'modalSlideIn 0.22s cubic-bezier(0.34,1.56,0.64,1)' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-28 pt-24 pb-20 border-b border-slate-100 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-16">
            {isEditMode ? (
              <Input
                value={formData?.productName}
                onChange={e => handleField('productName', e?.target?.value)}
                placeholder="Product name"
                className="text-xl font-bold"
              />
            ) : (
              <h2 className="text-2xl font-bold text-slate-900 leading-tight truncate">{product?.productName}</h2>
            )}
            <div className="flex items-center flex-wrap gap-8 mt-10">
              {/* Status badge */}
              <span className={`inline-flex items-center px-10 py-4 rounded-full text-xs font-semibold border capitalize ${getStatusColor(isEditMode ? formData?.catalogStatus : product?.catalogStatus)}`}>
                {isEditMode ? formData?.catalogStatus : product?.catalogStatus}
              </span>
              {/* Category badge */}
              {(isEditMode ? formData?.category : product?.category) && (
                <span className={`inline-flex px-8 py-4 rounded-md text-xs font-medium ${getCategoryColor(isEditMode ? formData?.category : product?.category)}`}>
                  {isEditMode
                    ? CATEGORY_OPTIONS?.find(c => c?.value === formData?.category)?.label || formData?.category
                    : categoryLabel}
                </span>
              )}
              {/* Margin badge */}
              {(isEditMode ? formData?.marginCategory : product?.marginCategory) && (
                <span className={`text-xs font-semibold capitalize ${getMarginColor(isEditMode ? formData?.marginCategory : product?.marginCategory)}`}>
                  {isEditMode ? formData?.marginCategory : product?.marginCategory} margin
                </span>
              )}
            </div>
          </div>

          {/* Header right: Edit/Save/Cancel + Close */}
          <div className="flex items-center gap-8 flex-shrink-0">
            {isEditMode ? (
              <>
                <Button variant="outline" onClick={() => setIsEditMode(false)} className="h-9 px-14 text-sm">
                  Cancel
                </Button>
                <Button variant="default" onClick={handleSave} disabled={saving} className="h-9 px-14 text-sm">
                  {saving ? (
                    <span className="flex items-center gap-6"><Icon name="Loader2" size={14} className="animate-spin" />Saving…</span>
                  ) : (
                    <span className="flex items-center gap-6"><Icon name="Check" size={14} />Save Changes</span>
                  )}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setIsEditMode(true)} className="h-9 px-14 text-sm">
                <span className="flex items-center gap-6"><Icon name="Edit" size={14} />Edit</span>
              </Button>
            )}
            <button
              onClick={onClose}
              className="p-8 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-28 py-24">
          {isEditMode ? (
            /* Edit Mode — Form Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
              <Select
                label="Category"
                options={CATEGORY_OPTIONS}
                value={formData?.category}
                onChange={v => handleField('category', v)}
                placeholder="Select category"
              />
              <Input
                label="OEM / Brand"
                placeholder="e.g., Cisco, Fortinet"
                value={formData?.oemBrand}
                onChange={e => handleField('oemBrand', e?.target?.value)}
              />
              <Input
                label="Model / SKU"
                placeholder="e.g., C9300-48P-E"
                value={formData?.modelSku}
                onChange={e => handleField('modelSku', e?.target?.value)}
              />
              <Select
                label="Licensing Model"
                options={LICENSING_OPTIONS}
                value={formData?.licensingModel}
                onChange={v => handleField('licensingModel', v)}
                placeholder="Select licensing"
              />
              <Input
                label="Target Industry"
                placeholder="e.g., Enterprise, SMB"
                value={formData?.targetIndustry}
                onChange={e => handleField('targetIndustry', e?.target?.value)}
              />
              <Input
                label="Price Range"
                placeholder="e.g., OMR 5,000–OMR 15,000"
                value={formData?.priceRange}
                onChange={e => handleField('priceRange', e?.target?.value)}
              />
              <Select
                label="Margin Category"
                options={MARGIN_OPTIONS}
                value={formData?.marginCategory}
                onChange={v => handleField('marginCategory', v)}
              />
              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={formData?.catalogStatus}
                onChange={v => handleField('catalogStatus', v)}
              />
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-6">Description</label>
                <textarea
                  className="w-full border border-slate-200 rounded-lg px-12 py-10 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                  rows={4}
                  placeholder="Brief product description..."
                  value={formData?.description}
                  onChange={e => handleField('description', e?.target?.value)}
                />
              </div>
            </div>
          ) : (
            /* View Mode — Two Column Detail Layout */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-32 gap-y-0">
              {/* Left Column */}
              <div className="space-y-0">
                <DetailRow icon="Tag" label="OEM / Brand" value={product?.oemBrand} />
                <DetailRow icon="Hash" label="Model / SKU" value={product?.modelSku} mono />
                <DetailRow icon="Layers" label="Licensing Model" value={licensingLabel} />
                <DetailRow icon="Building2" label="Target Industry" value={product?.targetIndustry} />
                <DetailRow icon="Banknote" label="Price Range" value={product?.priceRange} />
              </div>
              {/* Right Column */}
              <div className="space-y-0">
                <DetailRow icon="TrendingUp" label="Margin Category" value={product?.marginCategory} capitalize />
                <DetailRow icon="Grid3x3" label="Category" value={categoryLabel} />
                <DetailRow icon="Calendar" label="Created" value={product?.createdAt ? new Date(product?.createdAt)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null} />
                <DetailRow icon="RefreshCw" label="Last Updated" value={product?.updatedAt ? new Date(product?.updatedAt)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null} />
                <DetailRow icon="Fingerprint" label="Product ID" value={product?.id ? String(product?.id)?.slice(0, 8)?.toUpperCase() : null} mono />
              </div>

              {/* Description — full width */}
              {product?.description && (
                <div className="md:col-span-2 mt-20 pt-20 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-8">Description</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{product?.description}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Confirmation Prompt */}
        {confirmAction && (
          <div className="mx-28 mb-0 mt-0 px-20 py-16 rounded-xl border flex items-center justify-between gap-16 flex-shrink-0"
            style={{ background: confirmAction === 'delete' ? '#fef2f2' : '#fffbeb', borderColor: confirmAction === 'delete' ? '#fecaca' : '#fde68a' }}>
            <div className="flex items-center gap-10">
              <Icon name={confirmAction === 'delete' ? 'AlertTriangle' : 'Archive'} size={16}
                className={confirmAction === 'delete' ? 'text-red-500' : 'text-amber-500'} />
              <span className="text-sm font-medium" style={{ color: confirmAction === 'delete' ? '#b91c1c' : '#92400e' }}>
                {confirmAction === 'delete' ?'Permanently delete this product from the catalog?' :'Archive this product? Status will be set to Sunset.'}
              </span>
            </div>
            <div className="flex items-center gap-8 flex-shrink-0">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-12 py-6 rounded-lg text-xs font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction === 'delete' ? handleDeleteConfirm : handleArchiveConfirm}
                className={`px-12 py-6 rounded-lg text-xs font-medium text-white transition-colors ${
                  confirmAction === 'delete' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'
                }`}
              >
                {confirmAction === 'delete' ? 'Delete' : 'Archive'}
              </button>
            </div>
          </div>
        )}

        {/* Quick Actions Footer */}
        <div className="flex items-center justify-between gap-12 px-28 py-18 border-t border-slate-100 bg-slate-50/60 flex-shrink-0 mt-4">
          {/* Left: destructive actions */}
          <div className="flex items-center gap-8">
            <button
              onClick={() => setConfirmAction('archive')}
              className="flex items-center gap-6 px-14 py-8 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 transition-colors"
            >
              <Icon name="Archive" size={14} />
              Archive
            </button>
            <button
              onClick={() => setConfirmAction('delete')}
              className="flex items-center gap-6 px-14 py-8 rounded-lg text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors"
            >
              <Icon name="Trash2" size={14} />
              Delete
            </button>
          </div>

          {/* Right: status change only */}
          <div className="flex items-center gap-8">
            {/* Change Status dropdown */}
            <div className="relative" ref={statusRef}>
              <button
                onClick={() => setStatusDropdownOpen(prev => !prev)}
                className="flex items-center gap-6 px-14 py-8 rounded-lg text-sm font-medium text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 transition-colors"
              >
                <Icon name="RefreshCw" size={14} />
                Change Status
                <Icon name={statusDropdownOpen ? 'ChevronUp' : 'ChevronDown'} size={12} className="text-slate-400" />
              </button>
              {statusDropdownOpen && (
                <div className="absolute bottom-full mb-6 right-0 w-44 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-10">
                  {STATUS_OPTIONS?.map(opt => (
                    <button
                      key={opt?.value}
                      onClick={() => handleStatusChange(opt?.value)}
                      className={`w-full text-left px-14 py-10 text-sm transition-colors hover:bg-primary/5 flex items-center justify-between ${
                        (isEditMode ? formData?.catalogStatus : product?.catalogStatus) === opt?.value
                          ? 'bg-primary/10 text-primary font-semibold' :'text-slate-700'
                      }`}
                    >
                      <span>{opt?.label}</span>
                      {(isEditMode ? formData?.catalogStatus : product?.catalogStatus) === opt?.value && (
                        <Icon name="Check" size={13} className="text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from { opacity: 0; transform: scale(0.94) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

function DetailRow({ icon, label, value, mono, capitalize }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-10 py-12 border-b border-slate-50">
      <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0 mt-1">
        <Icon name={icon} size={13} className="text-slate-500" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-400 font-medium mb-1">{label}</p>
        <p className={`text-sm text-slate-800 font-medium ${mono ? 'font-mono' : ''} ${capitalize ? 'capitalize' : ''}`}>
          {value}
        </p>
      </div>
    </div>
  );
}
